const extractPreviewBodyHtml = (iframe) => {
  const doc = iframe?.contentDocument;
  if (!doc?.body) return "";
  return doc.body.innerHTML;
};

const labelOf = (element) =>
  String(
    element?.getAttribute("data-cke-tooltip-text") ||
      element?.getAttribute("aria-label") ||
      element?.textContent ||
      ""
  )
    .trim()
    .toLowerCase();

const restoreRange = (doc, range) => {
  if (!doc || !range) return false;
  const selection = doc.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

const insertHtml = (doc, html) => {
  try {
    doc.execCommand("insertHTML", false, html);
  } catch (_error) {
    const selection = doc.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const fragment = range.createContextualFragment(html);
    range.insertNode(fragment);
  }
};

const applyHeading = (doc, heading) => {
  const tag = String(heading || "").toLowerCase();
  if (tag === "paragraph" || tag === "p") {
    doc.execCommand("formatBlock", false, "p");
    return;
  }
  const match = tag.match(/heading\s*([1-6])/);
  if (match) {
    doc.execCommand("formatBlock", false, `h${match[1]}`);
  }
};

const applyLink = (doc) => {
  const selection = doc.getSelection();
  const selectedText = String(selection?.toString() || "").trim();
  const url = window.prompt("Enter link URL", "https://");
  if (!url) return;

  if (!selectedText) {
    insertHtml(doc, `<a href="${url.replace(/"/g, "&quot;")}">${url}</a>`);
    return;
  }
  doc.execCommand("createLink", false, url);
};

const applyImage = (doc, onDone) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      insertHtml(doc, `<img src="${reader.result}" alt="" />`);
      onDone();
    };
    reader.readAsDataURL(file);
  };
  input.click();
};

const applyTable = (doc) => {
  insertHtml(
    doc,
    `<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%"><tr><td>&nbsp;</td><td>&nbsp;</td></tr><tr><td>&nbsp;</td><td>&nbsp;</td></tr></table>`
  );
};

const applyMedia = (doc) => {
  const url = window.prompt("Enter media URL", "https://");
  if (!url) return;
  insertHtml(
    doc,
    `<p><a href="${url.replace(/"/g, "&quot;")}">${url}</a></p>`
  );
};

const runToolbarAction = (doc, target, onHtmlChange) => {
  const label = labelOf(target);
  if (!label) return false;

  if (label.includes("source") || target?.closest?.("[data-mail-source-btn]")) {
    return false;
  }

  if (label.includes("bold")) {
    doc.execCommand("bold");
  } else if (label.includes("italic")) {
    doc.execCommand("italic");
  } else if (label.includes("underline")) {
    doc.execCommand("underline");
  } else if (label.includes("undo")) {
    doc.execCommand("undo");
  } else if (label.includes("redo")) {
    doc.execCommand("redo");
  } else if (label.includes("numbered list") || label.includes("numbered")) {
    doc.execCommand("insertOrderedList");
  } else if (label.includes("bulleted list") || label.includes("bulleted")) {
    doc.execCommand("insertUnorderedList");
  } else if (label.includes("block quote") || label.includes("blockquote")) {
    doc.execCommand("formatBlock", false, "blockquote");
  } else if (label.includes("align left") || label === "left") {
    doc.execCommand("justifyLeft");
  } else if (label.includes("align center") || label === "center") {
    doc.execCommand("justifyCenter");
  } else if (label.includes("align right") || label === "right") {
    doc.execCommand("justifyRight");
  } else if (label.includes("justify")) {
    doc.execCommand("justifyFull");
  } else if (label.includes("link")) {
    applyLink(doc);
  } else if (label.includes("image")) {
    applyImage(doc, () => onHtmlChange(extractPreviewBodyHtml({ contentDocument: doc })));
    return true;
  } else if (label.includes("table")) {
    applyTable(doc);
  } else if (label.includes("media")) {
    applyMedia(doc);
  } else if (label.includes("heading") || label.includes("paragraph")) {
    applyHeading(doc, label);
  } else {
    return false;
  }

  onHtmlChange(extractPreviewBodyHtml({ contentDocument: doc }));
  return true;
};

export const insertHtmlAtPreviewSelection = (iframe, html) => {
  const doc = iframe?.contentDocument;
  if (!doc?.body) return false;
  doc.body.focus();
  insertHtml(doc, html);
  return true;
};

export const bindCkeditorToolbarToPreview = (
  editor,
  getIframe,
  isActive,
  onHtmlChange
) => {
  const toolbarElement = editor?.ui?.view?.toolbar?.element;
  if (!toolbarElement) return () => {};

  let savedRange = null;

  const rememberRange = () => {
    const iframe = getIframe();
    const doc = iframe?.contentDocument;
    const selection = doc?.getSelection();
    if (selection?.rangeCount) {
      savedRange = selection.getRangeAt(0).cloneRange();
      return;
    }
    if (iframe?.__mailPreviewRange) {
      savedRange = iframe.__mailPreviewRange;
    }
  };

  const preparePreviewDocument = () => {
    const iframe = getIframe();
    const doc = iframe?.contentDocument;
    if (!doc?.body) return null;
    iframe.contentWindow?.focus();
    doc.body.focus();
    restoreRange(doc, savedRange);
    return doc;
  };

  const handleMouseDown = (event) => {
    if (!isActive()) return;
    if (event.target?.closest?.("[data-mail-source-btn]")) return;
    if (!event.target?.closest?.(".ck-toolbar, .ck-dropdown__panel, .ck-list")) {
      return;
    }
    rememberRange();
    event.preventDefault();
  };

  const handleClick = (event) => {
    if (!isActive()) return;
    if (event.target?.closest?.("[data-mail-source-btn]")) return;

    const button = event.target?.closest?.(
      ".ck-button, .ck-list-item-button, .ck-heading_heading1, .ck-heading_heading2, .ck-heading_heading3, .ck-heading_paragraph"
    );
    if (!button) return;

    const doc = preparePreviewDocument();
    if (!doc) return;

    const handled = runToolbarAction(doc, button, onHtmlChange);
    if (!handled) return;

    event.preventDefault();
    event.stopPropagation();
  };

  toolbarElement.addEventListener("mousedown", handleMouseDown, true);
  toolbarElement.addEventListener("click", handleClick, true);
  document.addEventListener("mousedown", handleMouseDown, true);
  document.addEventListener("click", handleClick, true);

  return () => {
    toolbarElement.removeEventListener("mousedown", handleMouseDown, true);
    toolbarElement.removeEventListener("click", handleClick, true);
    document.removeEventListener("mousedown", handleMouseDown, true);
    document.removeEventListener("click", handleClick, true);
  };
};
